import { useCallback, useEffect, useMemo, useState, type KeyboardEvent, type ReactNode } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, ChevronRight, CircleAlert, ExternalLink, Loader2, RefreshCw } from "lucide-react";

import {
  BatchDetailLink,
  GosiDetailLink,
  SalesOrderDetailLink,
  ShipmentDetailLink,
} from "@/components/hub/HubEntityDetailLink";
import { QuickBooksTxnLink } from "@/components/hub/QuickBooksTxnLink";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { Input } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { ApiError } from "@/lib/api";
import { freshdeskTicketUrl } from "@/lib/freshdeskAgentUrl";
import { cn } from "@/lib/utils";
import {
  fetchGosiDetail,
  fetchGosiInvoiceList,
  fetchGosiSerials,
  fetchGosiSerialsPortal,
  filterGosiListRowsClient,
  type GosiDetail,
  type GosiListRow,
  type GosiSalesOrderBatchLine,
  type GosiSerialRow,
} from "@/pages/shipping/gosiDetailsApi";

const GOSI_DETAILS_HELP =
  "Megaventory sales invoice (GOSI) synced to the hub. Select a document for invoice header, linked sales order batch lines, device serials, and line detail.";

const PORTAL_COLUMNS_BLUR_DELAY_MS = 1000;
const WIRELESS_PROD_INACCESSIBLE_IN_DEV_MESSAGE =
  "The Wireless_Prod database is inaccessible in local development. Portal org and group load on the production hub only.";

function formatShortDate(value: string | null | undefined): string {
  if (value == null || value.trim() === "") return "—";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value.trim());
  if (match) {
    const [, yyyy, mm, dd] = match;
    return `${mm}-${dd}-${yyyy}`;
  }
  return value;
}

function formatMoney(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function megaventoryGosiUrl(mvId: number, documentNumber: string): string {
  const trimmedNumber = documentNumber.trim();
  const name = encodeURIComponent(
    trimmedNumber !== "" ? `Goods Out and Sales Invoice ${trimmedNumber}` : `Goods Out and Sales Invoice ${mvId}`,
  );
  return `https://smartgridsolutions.megaventory.com/inventory/default.aspx?document=GoodsOutbound&id=${mvId}&name=${name}`;
}

function wirelessPortalDeviceDetailsUrl(imei: string): string {
  return `https://portal-sgs.com/device-details/?id=${encodeURIComponent(imei.trim())}`;
}

function gosiSerialPortalFieldCell(
  value: string | null | undefined,
  options: {
    rowHasImei: boolean;
    pending: boolean;
    failed: boolean;
    errorMessage: string;
    fieldLabel: string;
  },
): ReactNode {
  if (options.pending) {
    return "Loading…";
  }
  if (options.failed && options.rowHasImei) {
    return (
      <HoverHintTrigger
        trigger={
          <CircleAlert
            className="h-3.5 w-3.5 shrink-0 text-red-600"
            aria-label={`${options.fieldLabel} could not be loaded from wireless portal`}
          />
        }
      >
        {options.errorMessage}
      </HoverHintTrigger>
    );
  }
  return value != null && value.trim() !== "" ? value : "—";
}

function formatAddressBlock(input: {
  recipient: string | null;
  line1: string | null;
  line2: string | null;
  line3: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
}): string | null {
  const cityLine = [input.city, input.state, input.zip]
    .filter((piece) => piece != null && String(piece).trim() !== "")
    .join(" ");
  const parts = [
    input.recipient,
    input.line1,
    input.line2,
    input.line3,
    cityLine.trim() !== "" ? cityLine : null,
    input.country,
  ].filter((piece) => piece != null && String(piece).trim() !== "");
  return parts.length > 0 ? parts.map(String).join("\n") : null;
}

function gosiStatusBadge(status: string | null): ReactNode {
  const normalized = (status ?? "").trim();
  if (normalized === "") {
    return (
      <Badge tone="muted" className="text-[10px]">
        Unknown
      </Badge>
    );
  }
  const lower = normalized.toLowerCase();
  const tone =
    lower.includes("approved") || lower.includes("closed")
      ? "success"
      : lower.includes("cancel")
        ? "warning"
        : "neutral";
  return (
    <Badge tone={tone} className="whitespace-nowrap px-1.5 text-[10px]">
      {normalized}
    </Badge>
  );
}

function GosiLinesTable({ lines }: { lines: GosiDetail["lines"] }) {
  if (lines.length === 0) {
    return <p className="text-sm text-slate-600">No line items on this GOSI.</p>;
  }

  const lineTotal = lines.reduce((sum, line) => {
    const quantity = Number(line.quantity ?? 0);
    const unitPrice = Number(line.unit_price ?? 0);
    if (!Number.isFinite(quantity) || !Number.isFinite(unitPrice)) {
      return sum;
    }
    return sum + quantity * unitPrice;
  }, 0);

  return (
    <div className="overflow-x-auto">
      <table className="w-full min-w-[640px] border-collapse text-left text-sm text-slate-800">
        <thead className="border-b border-slate-200 bg-slate-50/90 text-xs font-semibold uppercase tracking-wide text-slate-500">
          <tr>
            <th className="py-2 pl-4 pr-3">SKU</th>
            <th className="px-3 py-2">Description</th>
            <th className="px-3 py-2 text-right">Qty</th>
            <th className="py-2 pl-3 pr-4 text-right">Unit price</th>
            <th className="py-2 pl-3 pr-4 text-right">Line total</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {lines.map((line, index) => {
            const quantity = Number(line.quantity ?? 0);
            const unitPrice = Number(line.unit_price ?? 0);
            const extended =
              Number.isFinite(quantity) && Number.isFinite(unitPrice) ? quantity * unitPrice : null;
            return (
              <tr key={`${line.sku ?? "line"}-${index}`}>
                <td className="py-2 pl-4 pr-3 font-mono text-xs">{line.sku?.trim() || "—"}</td>
                <td className="px-3 py-2">{line.description?.trim() || "—"}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {Number.isFinite(quantity) ? quantity.toLocaleString() : "—"}
                </td>
                <td className="py-2 pl-3 pr-4 text-right tabular-nums">{formatMoney(unitPrice)}</td>
                <td className="py-2 pl-3 pr-4 text-right tabular-nums">{formatMoney(extended)}</td>
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          <tr className="border-t border-slate-200 bg-slate-50/60">
            <td colSpan={4} className="py-2 pl-4 pr-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">
              Total
            </td>
            <td className="py-2 pl-3 pr-4 text-right font-semibold tabular-nums text-slate-900">
              {formatMoney(lineTotal)}
            </td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function GosiSalesOrderLinesTable({
  salesOrder,
  lines,
}: {
  salesOrder: GosiDetail["sales_order"];
  lines: GosiSalesOrderBatchLine[];
}) {
  if (salesOrder == null) {
    return <p className="text-sm text-slate-600">No sales order is linked to this GOSI.</p>;
  }

  return (
    <div className="space-y-4">
      <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sales order</dt>
          <dd className="mt-0.5">
            <SalesOrderDetailLink
              salesOrderId={salesOrder.sales_order_id}
              salesOrderNumber={salesOrder.sales_order_number}
            />
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Customer PO</dt>
          <dd className="mt-0.5">{salesOrder.customer_purchase_order_number ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">SGS ship by</dt>
          <dd className="mt-0.5">{formatShortDate(salesOrder.sgs_ship_by_date)}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client</dt>
          <dd className="mt-0.5">{salesOrder.client_name ?? "—"}</dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Order quantity</dt>
          <dd className="mt-0.5 tabular-nums">
            {salesOrder.order_total_quantity != null ? salesOrder.order_total_quantity.toLocaleString() : "—"}
          </dd>
        </div>
        <div>
          <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Shipped quantity</dt>
          <dd className="mt-0.5 tabular-nums">{salesOrder.total_shipped_quantity.toLocaleString()}</dd>
        </div>
      </dl>

      {lines.length === 0 ? (
        <p className="text-sm text-slate-600">No batch lines on this sales order.</p>
      ) : (
        <div className="-mx-1 overflow-x-auto">
          <table className="w-full min-w-[720px] text-left text-xs text-slate-800">
            <thead className="border-b border-slate-200 text-[11px] font-semibold uppercase tracking-wide text-slate-600">
              <tr>
                <th className="px-2 py-1.5">Batch</th>
                <th className="px-2 py-1.5">SKU</th>
                <th className="px-2 py-1.5">Build order</th>
                <th className="px-2 py-1.5 text-right tabular-nums">Qty</th>
                <th className="px-2 py-1.5 text-right tabular-nums">Shipped</th>
                <th className="px-2 py-1.5 text-right tabular-nums">Assembled</th>
                <th className="px-2 py-1.5">SGS ship by</th>
                <th className="px-2 py-1.5">Shipment</th>
                <th className="px-2 py-1.5">TLA build / PO</th>
              </tr>
            </thead>
            <tbody>
              {lines.map((line) => (
                <tr key={line.batch_item_id} className="border-b border-slate-50 last:border-none">
                  <td className="px-2 py-1.5 align-top">
                    <BatchDetailLink batchId={line.batch_id} className="text-[11px]" />
                  </td>
                  <td className="min-w-0 px-2 py-1.5 align-top font-mono text-[11px] break-all">{line.sku || "—"}</td>
                  <td className="px-2 py-1.5 align-top">{line.build_order_number ?? "—"}</td>
                  <td className="px-2 py-1.5 text-right align-top tabular-nums">{line.batch_item_quantity}</td>
                  <td className="px-2 py-1.5 text-right align-top tabular-nums">{line.quantity_shipped}</td>
                  <td className="px-2 py-1.5 text-right align-top tabular-nums">{line.quantity_assembled}</td>
                  <td className="px-2 py-1.5 align-top">{formatShortDate(line.sgs_ship_by_date)}</td>
                  <td className="px-2 py-1.5 align-top tabular-nums">
                    {line.shipment_id != null ? (
                      <ShipmentDetailLink shipmentId={line.shipment_id} className="text-[11px]" />
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-2 py-1.5 align-top text-[11px]">
                    {line.tla_build_order_number != null || line.tla_purchase_order_number != null ? (
                      <span>
                        {line.tla_build_order_number ?? "—"}
                        {line.tla_purchase_order_number != null ? ` / ${line.tla_purchase_order_number}` : ""}
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function GosiSerialsPanel({
  mvDocumentId,
  serialCount,
}: {
  mvDocumentId: number;
  serialCount: number;
}) {
  const [serialsOpen, setSerialsOpen] = useState(false);

  const serialsQuery = useQuery<GosiSerialRow[], ApiError>({
    queryKey: ["shipping", "gosi-details", "serials", mvDocumentId],
    queryFn: () => fetchGosiSerials(mvDocumentId),
    enabled: serialsOpen && serialCount > 0,
  });

  const serialsHasImei = useMemo(
    () => (serialsQuery.data ?? []).some((row) => (row.imei ?? "").trim() !== ""),
    [serialsQuery.data],
  );
  const portalLookupDevBlocked = import.meta.env.DEV && serialsHasImei;

  const serialsPortalQuery = useQuery<
    Record<
      string,
      {
        wireless_portal_org_name: string | null;
        wireless_portal_group_names: string | null;
      }
    >,
    ApiError
  >({
    queryKey: ["shipping", "gosi-details", "serials-portal", mvDocumentId],
    queryFn: () => fetchGosiSerialsPortal(mvDocumentId),
    enabled: serialsOpen && serialCount > 0 && serialsHasImei && !import.meta.env.DEV,
  });

  const [portalColumnsBlurred, setPortalColumnsBlurred] = useState(false);
  useEffect(() => {
    if (portalLookupDevBlocked || !serialsHasImei || !serialsPortalQuery.isFetching) {
      setPortalColumnsBlurred(false);
      return;
    }
    setPortalColumnsBlurred(true);
    const timer = window.setTimeout(() => setPortalColumnsBlurred(false), PORTAL_COLUMNS_BLUR_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [portalLookupDevBlocked, serialsHasImei, serialsPortalQuery.isFetching, serialsPortalQuery.fetchStatus]);

  const displaySerialRows = useMemo(() => {
    const baseRows = serialsQuery.data ?? [];
    const portalByImei = serialsPortalQuery.data ?? {};
    return baseRows.map((row) => {
      const imei = (row.imei ?? "").trim();
      const portal = imei !== "" ? portalByImei[imei] : undefined;
      return {
        ...row,
        wireless_portal_org_name: portal?.wireless_portal_org_name ?? null,
        wireless_portal_group_names: portal?.wireless_portal_group_names ?? null,
      };
    });
  }, [serialsQuery.data, serialsPortalQuery.data]);

  const portalColumnsPending =
    serialsHasImei && portalColumnsBlurred && serialsPortalQuery.isFetching;
  const portalColumnsFailed =
    serialsHasImei && (portalLookupDevBlocked || serialsPortalQuery.isError);
  const portalColumnsErrorMessage = portalLookupDevBlocked
    ? WIRELESS_PROD_INACCESSIBLE_IN_DEV_MESSAGE
    : serialsPortalQuery.error?.message ?? "Wireless portal org and group could not be loaded.";

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 py-3">
        <CardTitleWithHelp hoverHint="Device label, IMEI, wireless portal org and group, and box label serials linked to this GOSI through its sales order batch lines. Device label opens hub device lookup; the external link opens the wireless portal device page when an IMEI is present.">
          Serial numbers
        </CardTitleWithHelp>
        <div className="flex flex-wrap items-center gap-2">
          {serialsHasImei && !import.meta.env.DEV ? (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={serialsPortalQuery.isFetching || !serialsOpen}
              onClick={() => {
                void serialsPortalQuery.refetch();
              }}
            >
              <RefreshCw
                className={cn("mr-1.5 h-3.5 w-3.5", serialsPortalQuery.isFetching && "animate-spin")}
                aria-hidden
              />
              Refresh portal
            </Button>
          ) : null}
          <Button
            type="button"
            size="sm"
            variant="secondary"
            aria-expanded={serialsOpen}
            onClick={() => setSerialsOpen((open) => !open)}
          >
            {serialsOpen ? (
              <ChevronDown className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            ) : (
              <ChevronRight className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            )}
            {serialsOpen ? "Hide serials" : `Show serials (${String(serialCount)})`}
          </Button>
        </div>
      </CardHeader>
      {serialsOpen ? (
        <CardBody className="p-0">
          {serialCount === 0 ? (
            <p className="px-4 py-3 text-sm text-slate-600">No serials linked to this sales order yet.</p>
          ) : null}
          {serialCount > 0 && serialsQuery.isPending && serialsQuery.data == null ? (
            <div className="flex items-center gap-2 px-4 py-3 text-sm text-slate-600">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Loading serials…
            </div>
          ) : null}
          {serialsQuery.isError ? (
            <p className="mx-4 my-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
              {serialsQuery.error.message}
            </p>
          ) : null}
          {serialsQuery.data != null ? (
            <div className="-mx-1">
              {displaySerialRows.length === 0 ? (
                <p className="px-4 py-3 text-sm text-slate-600">No serials linked to this sales order yet.</p>
              ) : (
                <div className="max-h-[min(28rem,50vh)] overflow-auto">
                  <table className="w-full min-w-[640px] border-collapse text-left text-xs text-slate-800">
                    <thead className="sticky top-0 z-[1] border-b border-slate-200 bg-white text-[11px] font-semibold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_0_rgb(226_232_240)]">
                      <tr>
                        <th className="px-2 py-1.5">Device label</th>
                        <th className="px-2 py-1.5">IMEI</th>
                        <th className="px-2 py-1.5">Portal org</th>
                        <th className="px-2 py-1.5">Portal group</th>
                        <th className="px-2 py-1.5">Finished SKU</th>
                        <th className="px-2 py-1.5">Box label</th>
                        <th className="px-2 py-1.5">Shipment</th>
                      </tr>
                    </thead>
                    <tbody>
                      {displaySerialRows.map((row) => {
                        const rowHasImei = (row.imei ?? "").trim() !== "";
                        return (
                          <tr key={row.serial_fci_id} className="border-b border-slate-50 last:border-none">
                            <td className="px-2 py-1.5 align-top font-mono text-[11px]">
                              {row.device_label_serial != null && row.device_label_serial.trim() !== "" ? (
                                <span className="inline-flex items-center gap-1">
                                  <Link
                                    className="font-medium text-brand-700 hover:text-brand-900"
                                    to={`/manufacturing/device-lookup?serial=${encodeURIComponent(row.device_label_serial)}`}
                                  >
                                    {row.device_label_serial}
                                  </Link>
                                  {row.imei != null && row.imei.trim() !== "" ? (
                                    <a
                                      href={wirelessPortalDeviceDetailsUrl(row.imei)}
                                      target="_blank"
                                      rel="noreferrer"
                                      className="inline-flex shrink-0 rounded text-slate-400 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                                      aria-label={`Open wireless portal for ${row.device_label_serial}`}
                                    >
                                      <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                                    </a>
                                  ) : null}
                                </span>
                              ) : (
                                "—"
                              )}
                            </td>
                            <td className="px-2 py-1.5 align-top font-mono text-[11px]">{row.imei ?? "—"}</td>
                            <td
                              className={cn(
                                "px-2 py-1.5 align-top transition-[filter] duration-200",
                                portalColumnsPending && "blur-sm select-none text-slate-400",
                              )}
                            >
                              {gosiSerialPortalFieldCell(row.wireless_portal_org_name, {
                                rowHasImei,
                                pending: portalColumnsPending,
                                failed: portalColumnsFailed,
                                errorMessage: portalColumnsErrorMessage,
                                fieldLabel: "Portal org",
                              })}
                            </td>
                            <td
                              className={cn(
                                "px-2 py-1.5 align-top transition-[filter] duration-200",
                                portalColumnsPending && "blur-sm select-none text-slate-400",
                              )}
                            >
                              {gosiSerialPortalFieldCell(row.wireless_portal_group_names, {
                                rowHasImei,
                                pending: portalColumnsPending,
                                failed: portalColumnsFailed,
                                errorMessage: portalColumnsErrorMessage,
                                fieldLabel: "Portal group",
                              })}
                            </td>
                            <td className="px-2 py-1.5 align-top font-mono text-[11px]">{row.finished_sku ?? "—"}</td>
                            <td className="px-2 py-1.5 align-top font-mono text-[11px]">{row.box_serial ?? "—"}</td>
                            <td className="px-2 py-1.5 align-top tabular-nums">
                              {row.shipment_id != null ? (
                                <ShipmentDetailLink shipmentId={row.shipment_id} className="text-[11px]" />
                              ) : (
                                "—"
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : null}
        </CardBody>
      ) : null}
    </Card>
  );
}

export function GosiDetailsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const mvDocumentIdParam = searchParams.get("mv_document_id") ?? "";
  const searchParam = searchParams.get("search") ?? "";
  const [searchText, setSearchText] = useState(() => searchParam);

  const selectedMvDocumentId = useMemo(() => {
    const parsed = Number.parseInt(mvDocumentIdParam.trim(), 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }, [mvDocumentIdParam]);

  useEffect(() => {
    setSearchText(searchParam);
  }, [searchParam]);

  const listQuery = useQuery<GosiListRow[], ApiError>({
    queryKey: ["shipping", "gosi-details", "list", searchParam.trim()],
    queryFn: () => fetchGosiInvoiceList(searchParam.trim(), 100),
    staleTime: 30_000,
  });

  const detailQuery = useQuery<GosiDetail, ApiError>({
    queryKey: ["shipping", "gosi-details", "detail", selectedMvDocumentId],
    queryFn: () => fetchGosiDetail(selectedMvDocumentId!),
    enabled: selectedMvDocumentId != null,
  });

  const filteredRows = useMemo(
    () => filterGosiListRowsClient(listQuery.data ?? [], searchText),
    [listQuery.data, searchText],
  );

  const searchActive = searchText.trim() !== "";

  const openGosi = useCallback(
    (mvDocumentId: number) => {
      const next = new URLSearchParams(searchParams);
      next.set("mv_document_id", String(mvDocumentId));
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  function updateSearchFilter(value: string): void {
    setSearchText(value);
    const next = new URLSearchParams(searchParams);
    const trimmed = value.trim();
    if (trimmed === "") {
      next.delete("search");
    } else {
      next.set("search", value);
    }
    setSearchParams(next, { replace: true });
  }

  function handleGosiRowKeyDown(event: KeyboardEvent<HTMLTableRowElement>, mvDocumentId: number): void {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      openGosi(mvDocumentId);
    }
  }

  const document = detailQuery.data?.document;
  const shipToBlock =
    document != null
      ? formatAddressBlock({
          recipient: document.ship_to_recipient,
          line1: document.ship_to_address_line_1,
          line2: document.ship_to_address_line_2,
          line3: document.ship_to_address_line_3,
          city: document.ship_to_city,
          state: document.ship_to_state,
          zip: document.ship_to_zip,
          country: document.ship_to_country,
        })
      : null;
  const billToBlock =
    document != null
      ? formatAddressBlock({
          recipient: document.bill_to_recipient,
          line1: document.bill_to_address_line_1,
          line2: document.bill_to_address_line_2,
          line3: document.bill_to_address_line_3,
          city: document.bill_to_city,
          state: document.bill_to_state,
          zip: document.bill_to_zip,
          country: document.bill_to_country,
        })
      : null;

  const listCountLabel = (
    <span className="font-normal text-slate-600">
      ({filteredRows.length}
      {searchActive && listQuery.data != null ? ` of ${String(listQuery.data.length)}` : ""})
    </span>
  );

  const gosiNumber = document?.number != null ? String(document.number).trim() : "";
  const tabBreadcrumbLabel =
    gosiNumber.length > 0
      ? `GOSI ${gosiNumber}`
      : selectedMvDocumentId != null
        ? `GOSI ${String(selectedMvDocumentId)}`
        : "GOSI details";

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
        <PageTitleWithHelp hoverHint={GOSI_DETAILS_HELP} breadcrumbLabel={tabBreadcrumbLabel}>
          GOSI details
        </PageTitleWithHelp>
        {selectedMvDocumentId != null ? (
          <div className="flex flex-wrap gap-2">
            {document != null ? (
              <a
                href={megaventoryGosiUrl(document.mv_id, document.number)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex h-8 items-center rounded-md border border-slate-200 bg-white px-3 text-sm font-medium text-slate-800 hover:bg-slate-50"
              >
                <ExternalLink className="mr-1.5 h-3.5 w-3.5" aria-hidden />
                Open in Megaventory
              </a>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void detailQuery.refetch()}
              disabled={detailQuery.isFetching}
            >
              {detailQuery.isFetching ? (
                <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
              )}
              Refresh
            </Button>
          </div>
        ) : null}
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardBody>
          <label className="mb-1 block text-xs font-medium text-slate-700" htmlFor="gosi-details-search">
            Search GOSIs
          </label>
          <Input
            id="gosi-details-search"
            type="search"
            className="h-10"
            placeholder="MV document id, invoice number, GSO, customer PO, client, end user…"
            value={searchText}
            onChange={(event) => updateSearchFilter(event.target.value)}
          />
        </CardBody>
      </Card>

      <div className="grid min-h-[28rem] gap-4 lg:grid-cols-[minmax(20rem,26rem)_minmax(0,1fr)] lg:items-stretch">
        <HubViewportCard>
          <CardHeader className="flex shrink-0 flex-row flex-wrap items-center justify-between gap-2">
            <CardTitle>GOSIs {listCountLabel}</CardTitle>
            {listQuery.isFetching ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" aria-hidden /> : null}
          </CardHeader>
          <CardBody className="flex min-h-0 flex-1 flex-col p-0">
            {listQuery.isError ? (
              <p className="mx-5 my-4 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800">
                {listQuery.error.message}
              </p>
            ) : null}
            {listQuery.isLoading ? (
              <p className="px-5 py-4 text-sm text-slate-600">Loading GOSIs…</p>
            ) : null}
            {listQuery.data != null && filteredRows.length === 0 && !listQuery.isLoading ? (
              <p className="px-5 py-4 text-sm text-slate-600">No GOSIs match these filters.</p>
            ) : null}
            {filteredRows.length > 0 ? (
              <div className="min-h-0 flex-1 overflow-y-auto">
                <table className="w-full table-fixed border-collapse text-left text-xs text-slate-800">
                  <colgroup>
                    <col className="w-[5.5rem]" />
                    <col />
                    <col className="w-[5.5rem]" />
                  </colgroup>
                  <thead className="sticky top-0 z-10 border-b border-slate-200 bg-white text-[11px] font-semibold uppercase tracking-wide text-slate-600 shadow-[0_1px_0_0_rgb(226_232_240)]">
                    <tr>
                      <th className="px-3 py-2">Invoice</th>
                      <th className="px-2 py-2">Sales order</th>
                      <th className="px-3 py-2">Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row) => {
                      const isSelected = selectedMvDocumentId === row.mv_id;
                      return (
                        <tr
                          key={row.mv_id}
                          role="button"
                          tabIndex={0}
                          aria-selected={isSelected}
                          onClick={() => openGosi(row.mv_id)}
                          onKeyDown={(event) => handleGosiRowKeyDown(event, row.mv_id)}
                          className={cn(
                            "cursor-pointer border-b border-slate-50 last:border-none hover:bg-slate-50/90",
                            isSelected && "bg-sky-50 ring-1 ring-inset ring-sky-200",
                          )}
                        >
                          <td className="px-3 py-2 align-top">
                            <div className="font-mono tabular-nums font-medium text-slate-900">{row.number}</div>
                            <div className="mt-0.5">{gosiStatusBadge(row.status)}</div>
                          </td>
                          <td className="px-2 py-2 align-top">
                            <div className="truncate font-medium text-slate-900">
                              {row.sales_order_number ?? "—"}
                            </div>
                            {row.customer_purchase_order_number ? (
                              <div className="truncate text-[11px] text-slate-500">
                                PO {row.customer_purchase_order_number}
                              </div>
                            ) : null}
                            {row.end_user_name ? (
                              <div className="truncate text-[11px] text-slate-500">{row.end_user_name}</div>
                            ) : null}
                          </td>
                          <td className="px-3 py-2 align-top whitespace-nowrap tabular-nums">
                            {formatShortDate(row.date_created)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : null}
          </CardBody>
        </HubViewportCard>

        <div className="flex min-h-0 min-w-0 flex-col gap-4">
          {selectedMvDocumentId == null ? (
            <Card>
              <CardBody>
                <p className="text-sm text-slate-600">
                  Select a GOSI from the list or follow a GOSI link from Paperwork or another hub page.
                </p>
              </CardBody>
            </Card>
          ) : detailQuery.isLoading ? (
            <Card>
              <CardBody>
                <p className="text-sm text-slate-500">Loading GOSI…</p>
              </CardBody>
            </Card>
          ) : detailQuery.isError ? (
            <Card>
              <CardBody>
                <p className="text-sm text-red-700">{detailQuery.error.message}</p>
              </CardBody>
            </Card>
          ) : document != null && detailQuery.data != null ? (
            <>
              <Card>
                <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-2">
                  <div className="space-y-2">
                    <div className="flex min-w-0 items-center gap-1.5">
                      <CardTitle className="min-w-0">
                        GOSI{" "}
                        <GosiDetailLink mvDocumentId={document.mv_id} gosiDocumentNumber={document.number} />
                      </CardTitle>
                      <a
                        href={megaventoryGosiUrl(document.mv_id, document.number)}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="shrink-0 text-brand-600 hover:text-brand-700"
                        aria-label={`Open GOSI ${document.number} in Megaventory`}
                      >
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden />
                      </a>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">{gosiStatusBadge(document.status)}</div>
                  </div>
                </CardHeader>
                <CardBody className="grid gap-4 lg:grid-cols-3">
                  <dl className="space-y-2 text-sm text-slate-800">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">MV document</dt>
                      <dd className="mt-0.5 tabular-nums">{document.mv_id}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Sales order</dt>
                      <dd className="mt-0.5">
                        {document.sales_order_id != null ? (
                          <SalesOrderDetailLink
                            salesOrderId={document.sales_order_id}
                            salesOrderNumber={document.sales_order_number}
                          />
                        ) : (
                          document.sales_order_number ?? "—"
                        )}
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Customer PO</dt>
                      <dd className="mt-0.5">{document.customer_purchase_order_number ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Client</dt>
                      <dd className="mt-0.5">{document.client_name ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">End user</dt>
                      <dd className="mt-0.5">{document.end_user_name ?? "—"}</dd>
                    </div>
                  </dl>
                  <dl className="space-y-2 text-sm text-slate-800">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Created</dt>
                      <dd className="mt-0.5">{formatShortDate(document.date_created)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">MV updated</dt>
                      <dd className="mt-0.5">{formatShortDate(document.mv_updated)}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Terms</dt>
                      <dd className="mt-0.5">{document.terms ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">QuickBooks id</dt>
                      <dd className="mt-0.5">
                        <QuickBooksTxnLink
                          txnId={document.qb_id}
                          kind="invoice"
                          documentLabel={gosiNumber.length > 0 ? `GOSI ${gosiNumber}` : "this GOSI"}
                        />
                      </dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                        Freshdesk ticket
                      </dt>
                      <dd className="mt-0.5">
                        {document.commission_je_support_ticket_id != null &&
                        document.commission_je_support_ticket_id > 0 ? (
                          <a
                            href={freshdeskTicketUrl(document.commission_je_support_ticket_id)}
                            target="_blank"
                            rel="noreferrer noopener"
                            className="inline-flex items-center gap-1 text-brand-600 hover:text-brand-700"
                          >
                            #{document.commission_je_support_ticket_id}
                            <ExternalLink className="h-3 w-3" aria-hidden />
                          </a>
                        ) : (
                          "—"
                        )}
                      </dd>
                    </div>
                  </dl>
                  <dl className="space-y-2 text-sm text-slate-800">
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Ship to</dt>
                      <dd className="mt-0.5 whitespace-pre-wrap">{shipToBlock ?? "—"}</dd>
                    </div>
                    <div>
                      <dt className="text-xs font-semibold uppercase tracking-wide text-slate-500">Bill to</dt>
                      <dd className="mt-0.5 whitespace-pre-wrap">{billToBlock ?? "—"}</dd>
                    </div>
                  </dl>
                </CardBody>
              </Card>

              {detailQuery.data.sales_order != null || detailQuery.data.sales_order_lines.length > 0 ? (
                <Card>
                  <CardHeader>
                    <CardTitleWithHelp hoverHint="Batch lines and fulfillment context for the sales order linked to this GOSI, matching the batch details layout.">
                      Sales order details
                    </CardTitleWithHelp>
                  </CardHeader>
                  <CardBody>
                    <GosiSalesOrderLinesTable
                      salesOrder={detailQuery.data.sales_order}
                      lines={detailQuery.data.sales_order_lines}
                    />
                  </CardBody>
                </Card>
              ) : null}

              <GosiSerialsPanel
                key={selectedMvDocumentId}
                mvDocumentId={selectedMvDocumentId}
                serialCount={detailQuery.data.serial_count}
              />

              <Card>
                <CardHeader>
                  <CardTitle>Line items</CardTitle>
                </CardHeader>
                <CardBody className="p-0">
                  <GosiLinesTable lines={detailQuery.data.lines} />
                </CardBody>
              </Card>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
